import Box from '@amzn/awsui-components-react/polaris/box';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import Container from '@amzn/awsui-components-react/polaris/container';
import Form from '@amzn/awsui-components-react/polaris/form';
import Header from '@amzn/awsui-components-react/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import TextContent from '@amzn/awsui-components-react/polaris/text-content';
import {
    BaseMarkdown,
    CheckboxField,
    FormikField,
    ValueWithLabel,
} from '@amzn/event-engine-js-utils';
import { format } from 'date-fns';
import { FormikContextType, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { i18nStrings } from 'utils/constants';

import {
    FormSchemaType,
    Step,
    StepValidator,
} from './../../JoinEvent.validation';
import styles from './ReviewAndJoin.module.scss';

export const isStepTwoValid: StepValidator = async (
    formik
): Promise<boolean> => {
    try {
        const validatedFormErrors = await formik.validateForm(formik.values);
        return !validatedFormErrors[Step.REVIEW_STEP_TWO];
    } catch (e) {
        return false;
    }
};

export const generateFormFieldPrefix = (
    str: keyof FormSchemaType[Step.REVIEW_STEP_TWO]
) => {
    return `${[
        Step.REVIEW_STEP_TWO,
    ]}.${str}` as keyof FormSchemaType[Step.REVIEW_STEP_TWO];
};

interface ReviewAndJoinProps {
    isValidating?: boolean;
}

const ReviewAndJoin = ({ isValidating }: ReviewAndJoinProps) => {
    const formik: FormikContextType<FormSchemaType> = useFormikContext();

    useEffect(() => {
        formik.setErrors({});

        return () => {
            formik.setFieldValue(
                generateFormFieldPrefix('termsAndConditions'),
                false
            );
        };
    }, []);

    return (
        <SpaceBetween size="l">
            <Container
                header={<Header variant="h2">Event details</Header>}
                footer={
                    <Box padding={{ vertical: 'xs' }}>
                        <ValueWithLabel label="Description">
                            {formik.values[Step.REVIEW_STEP_TWO].description}
                        </ValueWithLabel>
                    </Box>
                }>
                <ColumnLayout columns={4} variant="text-grid">
                    <ValueWithLabel label="Name">
                        {formik.values[Step.REVIEW_STEP_TWO].eventName}
                    </ValueWithLabel>
                    <ValueWithLabel label="Start time">
                        {format(
                            new Date(
                                formik.values[Step.REVIEW_STEP_TWO].startTime
                            ),
                            'M/dd/yyyy hh:mm a'
                        )}
                    </ValueWithLabel>
                    <ValueWithLabel label="Duration">
                        {formik.values[Step.REVIEW_STEP_TWO].duration}{' '}
                        {formik.values[Step.REVIEW_STEP_TWO].duration === 1
                            ? 'hour'
                            : 'hours'}
                    </ValueWithLabel>
                    <ValueWithLabel label="Level">
                        {formik.values[Step.REVIEW_STEP_TWO].level}
                    </ValueWithLabel>
                </ColumnLayout>
            </Container>
            <Form>
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Read and accept before joining the event">
                            Terms and Conditions
                        </Header>
                    }>
                    <SpaceBetween size="l">
                        <TextContent>
                            <BaseMarkdown
                                className={styles.markdown}
                                linkTarget="_blank">
                                {i18nStrings.termsAndConditions}
                            </BaseMarkdown>
                        </TextContent>
                        <FormikField<FormSchemaType[Step.REVIEW_STEP_TWO]>
                            name={generateFormFieldPrefix('termsAndConditions')}
                            formFieldProps={{
                                stretch: true,
                                children: (
                                    <CheckboxField<
                                        FormSchemaType[Step.REVIEW_STEP_TWO]
                                    >
                                        name={generateFormFieldPrefix(
                                            'termsAndConditions'
                                        )}
                                        checkboxProps={{
                                            children:
                                                'I agree with the Terms and Conditions',
                                            disabled: isValidating,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </SpaceBetween>
                </Container>
            </Form>
        </SpaceBetween>
    );
};

export default ReviewAndJoin;
